import type { OrderData } from '@/types';
import { dateFormatter } from '@/utils/dateFormatter';

import { OrderItem } from './components/OrderItem';

import { ButtonElement } from '../shared/buttonElement/ButtonElement';
import { SectionElement } from '../shared/sectionElement/SectionElement';
import type { ManageOrdersElementProps } from './ManageOrdersElement.types';

export function ManageOrdersElement(props: ManageOrdersElementProps) {
  const {
    translations,
    element,
    children,
    handleViewReceipt,
    handleClickReviewProduct,
    pendingReceiptOrderId,
    onAccessFulfillment,
    onAddToCalendar,
  } = props;

  const { orders = [] } = element.attrs?.data || {};

  const headerItems = (order: OrderData) => {
    const labelClassName = 'text-sm text-[#111827] font-medium leading-5 w-[100px] sm:w-auto';
    const valueClassName = 'text-sm text-[#6B7280] font-normal leading-5';

    return (
      <div key={order.id} className="flex flex-col sm:flex-row justify-between sm:items-center w-full gap-2 sm:gap-6">
        <div className="flex sm:flex-col gap-1">
          <h5 className={labelClassName}>{translations?.['manage.orders.order.number']}</h5>
          <p className={valueClassName}>#{order.display_order_number}</p>
        </div>
        <div className="flex sm:flex-col gap-1">
          <h5 className={labelClassName}>{translations?.['manage.orders.order.creation_date']}</h5>
          <p className={valueClassName}>{dateFormatter({}).format(new Date(order.created_at))}</p>
        </div>
        {!order.free && (
          <div className="flex sm:flex-col gap-1">
            <h5 className={labelClassName}>{translations?.['manage.orders.order.total_amount']}</h5>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {order.discount_amount_cents > 0 && (
                  <p className={`${valueClassName} line-through`}>{order.formatted_original_total}</p>
                )}
                <p className={valueClassName}>{order.formatted_total}</p>
              </div>
              {order.refunded_amount_cents > 0 && (
                <div className="mt-1">
                  <p className="text-xs text-[#6B7280] font-normal leading-4">
                    {order.formatted_refunded_amount} {order.refund_status === 'pending' ? 'refunding' : 'refunded'}
                  </p>
                  {order.refund_note && (
                    <p className="text-xs text-[#6B7280] font-normal leading-4 mt-1 italic max-w-xs">
                      Note: {order.refund_note}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {order.discount_amount_cents > 0 && (
          <div className="flex sm:flex-col gap-1">
            <h5 className={labelClassName}>{translations?.['manage.orders.order.discount'] || 'Discount'}</h5>
            <p className={valueClassName}>-{order.formatted_discount}</p>
          </div>
        )}
        {order.can_view_receipt && (
          <div className="w-full sm:w-auto flex flex-col gap-1">
            <ButtonElement
              variant="default"
              disabled={pendingReceiptOrderId === order.id}
              onClick={() => (handleViewReceipt ? handleViewReceipt(order.id) : () => {})}
            >
              {translations?.['manage.orders.order.view_receipt_cta']}
            </ButtonElement>
          </div>
        )}
      </div>
    );
  };

  return (
    <SectionElement
      title={translations?.['manage.orders.title'] || 'Order history'}
      subTitle={translations?.['manage.orders.description'] || 'View your past orders and invoices'}
    >
      <div className="flex flex-col gap-y-4" id="current-plans-container">
        {orders.map((order) => {
          return (
            <div key={order.id} className="rounded-md border border-[#E5E7EB] overflow-hidden">
              <div className="flex items-center p-6">{headerItems(order)}</div>
              <div className="border-t border-[#E5E7EB] flex flex-col">
                {order.order_items.map((orderItem) => (
                  <OrderItem
                    key={orderItem.id}
                    orderItem={orderItem}
                    deliveryDate={order.created_at}
                    translations={translations}
                    onAccessFulfillment={onAccessFulfillment}
                    onAddToCalendar={onAddToCalendar}
                    onClickReviewProduct={
                      handleClickReviewProduct
                        ? () => handleClickReviewProduct?.(order.id, orderItem.id, orderItem.product_slug)
                        : undefined
                    }
                    hidePrice={order.order_items.length === 1}
                  />
                ))}
              </div>
            </div>
          );
        })}
        {children}
      </div>
    </SectionElement>
  );
}
